import { apiClient } from '../utils/api';
import { UrlBuilder } from '../utils/urls';
import { CreditTransactionItem, CreditTransactionFetchFilter, TransactionPagination } from '../models';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: number;
}

class CreditPointsService {
  // Get credit points balance for a user with token
  async getBalance(token: string, userId: number): Promise<ApiResponse<{ balance: number }>> {
    try {
      const response = await apiClient.getWithToken<{ balance: number }>(
        UrlBuilder.credits.balance(userId),
        token
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Get credit transactions for a user with token
  async getTransactions(token: string, userId: number, filters?: CreditTransactionFetchFilter): Promise<ApiResponse<{ transactions: CreditTransactionItem[], pagination: TransactionPagination }>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const url = UrlBuilder.credits.find(userId) + (queryParams.toString() ? `?${queryParams.toString()}` : '');
      const response = await apiClient.getWithToken<{ transactions: CreditTransactionItem[], pagination: TransactionPagination }>(url, token);

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Handle credit webhook (for payment processing - may not require token)
  async handleWebhook(userId: number, data: any): Promise<ApiResponse> {
    try {
      const response = await apiClient.post(
        UrlBuilder.credits.webhook(userId),
        data
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }
}

export const creditPointsService = new CreditPointsService();